import { CommonModule } from '@angular/common';
import {
  Component,
  Output,
  EventEmitter,
  Input,
  ElementRef,
  ViewChild,
} from '@angular/core';
import { FlexLayoutModule } from '@ngbracket/ngx-layout';
import { TranslateModule } from '@ngx-translate/core';

@Component({
    selector: 'app-upload-file',
    imports: [CommonModule, FlexLayoutModule, TranslateModule],
    templateUrl: './upload-file.component.html',
    styleUrls: ['./upload-file.component.scss']
})
export class UploadFileComponent {
  /** Holds currently selected image file before upload. */
  selectedFile: File | null = null;
  /** Display URL: internal preview when user selects file, or from parent when loaded from API */
  private _displayUrl = '';
  /** Receives image URL from parent input and refreshes displayed image source. */
  @Input() set imageUrl(value: string) {
    this._imageUrl = value || '';
    this._displayUrl = this._previewUrl || this._imageUrl;
  }
  get imageUrl(): string {
    return this._displayUrl;
  }
  /** Stores parent-provided image URL for initial display. */
  private _imageUrl = '';
  /** Stores preview URL generated from selected file. */
  private _previewUrl = '';
  /** Marks upload field as required in UI. */
  @Input() isRequired: boolean = false;
  /** Variant: 'default' | 'business-logo' - business-logo shows label + photo size description like the registration form */
  @Input() variant: 'default' | 'business-logo' = 'default';
  /** Max file size in bytes. Default 20 MB */
  @Input() maxSizeBytes = 20 * 1024 * 1024;
  /** Emits selected file to parent component. */
  @Output() getFile = new EventEmitter<File | null>();
  /** Emits when selected file exceeds max size */
  @Output() fileSizeError = new EventEmitter<void>();
  /** Emits the data URL when a file is selected so the parent can display the preview */
  @Output() previewUrlChange = new EventEmitter<string>();
  /** Emits when current file/image is cleared by user */
  @Output() fileCleared = new EventEmitter<void>();
  /** References hidden file input element to trigger picker/reset value. */
  @ViewChild('fileInput') fileInput: ElementRef<HTMLInputElement>;

  /** Handles file input change, validates size, and starts preview flow. */
  onFileSelected(event: any) {
    const file = event?.target?.files?.[0];
    if (!file) return;
    if (file.size > this.maxSizeBytes) {
      this.fileSizeError.emit();
      event.target.value = '';
      return;
    }
    this.selectedFile = file;
    this.getFile.emit(this.selectedFile);
    this.previewImage(this.selectedFile);
    event.target.value = '';
  }

  /** Generates base64 preview URL for selected image file. */
  previewImage(file: File) {
    const reader = new FileReader();
    reader.onload = (e: any) => {
      const dataUrl = e.target?.result as string;
      this._previewUrl = dataUrl;
      this._displayUrl = dataUrl;
      this.previewUrlChange.emit(dataUrl);
    };
    reader.readAsDataURL(file);
  }

  /** Opens native file picker dialog. */
  onOpenFile(): void {
    this.fileInput.nativeElement.click();
  }

  /** Clears selected image, preview, and notifies parent about reset state. */
  clearFile(event?: Event): void {
    event?.stopPropagation();
    event?.preventDefault();
    this.selectedFile = null;
    this._previewUrl = '';
    this._displayUrl = '';
    this.getFile.emit(null);
    this.previewUrlChange.emit('');
    this.fileCleared.emit();
    if (this.fileInput?.nativeElement) {
      this.fileInput.nativeElement.value = '';
    }
  }
}
